import React, { useState, useEffect, useCallback } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableRow, Paper,
    Dialog, DialogTitle, DialogContent, DialogActions, Grid, IconButton, Tooltip, Chip, Icon,
    Autocomplete, CircularProgress
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSnackbar from "components/MDSnackbar";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import { get, post, del } from "utils/WebService";
import { useMaterialUIController } from "context";

export default function Juridico({ recordId }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    // Processos Oficiais (TbProcesso via contratos)
    const [processosOficiais, setProcessosOficiais] = useState([]);
    const [loadingOficiais, setLoadingOficiais] = useState(true);

    // Processos Transitórios (tbDevedorCarteiraProcesso / BVJuridico / Manual)
    const [processosTransitorios, setProcessosTransitorios] = useState([]);
    const [loadingTransitorios, setLoadingTransitorios] = useState(true);

    // Lista de Tipos de Ação disponíveis
    const [acoesDisponiveis, setAcoesDisponiveis] = useState([]);

    // Modal de Cadastro/Edição
    const [openModal, setOpenModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        id: null,
        numeroProcesso: "",
        tipoAcao: "",
        codAcao: null,
        contrato: "",
        dataDistribuicao: "",
        valorCausa: "",
        observacao: ""
    });

    // Modal de Histórico / Auditoria
    const [openAuditModal, setOpenAuditModal] = useState(false);
    const [loadingAudit, setLoadingAudit] = useState(false);
    const [auditLogs, setAuditLogs] = useState([]);
    const [selectedProcessoAudit, setSelectedProcessoAudit] = useState(null);

    // Modal de Vinculação
    const [openLinkModal, setOpenLinkModal] = useState(false);
    const [selectedTransitorio, setSelectedTransitorio] = useState(null);
    const [idProcessoRealInput, setIdProcessoRealInput] = useState("");
    const [linking, setLinking] = useState(false);

    // Snackbar
    const [sbOpen, setSbOpen] = useState(false);
    const [sbColor, setSbColor] = useState("success");
    const [sbMessage, setSbMessage] = useState("");

    const notify = (msg, color = "success") => {
        setSbMessage(msg);
        setSbColor(color);
        setSbOpen(true);
    };

    const loadProcessosOficiais = useCallback(() => {
        if (!recordId) return;
        setLoadingOficiais(true);
        get(`/contratos/list/${recordId}`)
            .then(res => {
                if (res) {
                    const uniqueProcessos = [];
                    res.forEach(c => {
                        if (c.idProcesso && !uniqueProcessos.some(p => p.idProcesso === c.idProcesso)) {
                            uniqueProcessos.push({
                                idProcesso: c.idProcesso,
                                numeroProcesso: c.numeroProcesso,
                                dataDistribuicao: c.dataDistribuicao,
                                valorCausa: c.valorCausa,
                                equipeResponsavel: c.equipeResponsavel,
                                advogadoResponsavel: c.advogadoResponsavel,
                                contratosLinked: res.filter(x => x.idProcesso === c.idProcesso).map(x => x.titulo).join(", ")
                            });
                        }
                    });
                    setProcessosOficiais(uniqueProcessos);
                }
            })
            .catch(err => console.error("Erro ao carregar processos oficiais:", err))
            .finally(() => setLoadingOficiais(false));
    }, [recordId]);

    const loadProcessosTransitorios = useCallback(() => {
        if (!recordId) return;
        setLoadingTransitorios(true);
        get(`/processos-transitorios/by-devedor/${recordId}`)
            .then(res => {
                if (Array.isArray(res)) {
                    setProcessosTransitorios(res);
                }
            })
            .catch(err => console.error("Erro ao carregar processos da ficha:", err))
            .finally(() => setLoadingTransitorios(false));
    }, [recordId]);

    const loadAcoes = useCallback(() => {
        get("/processos-transitorios/acoes")
            .then(res => {
                if (Array.isArray(res)) {
                    setAcoesDisponiveis(res);
                }
            })
            .catch(err => console.error("Erro ao carregar ações:", err));
    }, []);

    useEffect(() => {
        loadProcessosOficiais();
        loadProcessosTransitorios();
        loadAcoes();
    }, [loadProcessosOficiais, loadProcessosTransitorios, loadAcoes]);

    const handleOpenNovo = () => {
        setFormData({
            id: null,
            numeroProcesso: "",
            tipoAcao: "",
            codAcao: null,
            contrato: "",
            dataDistribuicao: "",
            valorCausa: "",
            observacao: ""
        });
        setOpenModal(true);
    };

    const handleOpenEditar = (item) => {
        let dataDistStr = "";
        if (item.dataDistribuicao) {
            try {
                dataDistStr = new Date(item.dataDistribuicao).toISOString().split("T")[0];
            } catch (e) {
                dataDistStr = "";
            }
        }
        setFormData({
            id: item.id,
            numeroProcesso: item.numeroProcesso || "",
            tipoAcao: item.tipoAcao || "",
            codAcao: item.codAcao || null,
            contrato: item.contrato || "",
            dataDistribuicao: dataDistStr,
            valorCausa: item.valorCausa != null ? item.valorCausa : "",
            observacao: item.observacao || ""
        });
        setOpenModal(true);
    };

    const handleOpenHistorico = (item) => {
        setSelectedProcessoAudit(item);
        setOpenAuditModal(true);
        setLoadingAudit(true);
        get(`/processos-transitorios/${item.id}/historico`)
            .then(res => {
                setAuditLogs(Array.isArray(res) ? res : []);
            })
            .catch(err => {
                console.error("Erro ao carregar auditoria:", err);
                setAuditLogs([]);
            })
            .finally(() => setLoadingAudit(false));
    };

    const handleSave = () => {
        if (!formData.numeroProcesso && !formData.contrato) {
            notify("Informe ao menos o Número do Processo ou o Contrato.", "warning");
            return;
        }

        setSaving(true);
        const payload = {
            id: formData.id,
            idDevedor: recordId,
            numeroProcesso: formData.numeroProcesso,
            tipoAcao: formData.tipoAcao,
            codAcao: formData.codAcao,
            contrato: formData.contrato,
            dataDistribuicao: formData.dataDistribuicao || null,
            valorCausa: formData.valorCausa ? parseFloat(formData.valorCausa) : null,
            observacao: formData.observacao
        };

        post("/processos-transitorios", payload)
            .then(() => {
                notify("Processo salvo com sucesso!", "success");
                setOpenModal(false);
                loadProcessosTransitorios();
            })
            .catch(err => {
                notify(err.message || "Erro ao salvar processo.", "error");
            })
            .finally(() => setSaving(false));
    };

    const handleDelete = (item) => {
        if (window.confirm(`Deseja realmente excluir o processo "${item.numeroProcesso || item.contrato}"?`)) {
            del(`/processos-transitorios/${item.id}`)
                .then(() => {
                    notify("Processo excluído com sucesso!", "success");
                    loadProcessosTransitorios();
                })
                .catch(err => notify(err.message || "Erro ao excluir processo.", "error"));
        }
    };

    const handleOpenLink = (item) => {
        setSelectedTransitorio(item);
        setIdProcessoRealInput("");
        setOpenLinkModal(true);
    };

    const handleConfirmLink = () => {
        if (!idProcessoRealInput || !idProcessoRealInput.trim()) {
            notify("Informe o ID do Processo Oficial para vincular.", "warning");
            return;
        }

        setLinking(true);
        post("/processos-transitorios/vincular-processo-real", {
            idProcessoTransitorio: selectedTransitorio.id,
            idProcessoReal: idProcessoRealInput.trim()
        })
            .then(res => {
                notify(res.mensagem || "Processo vinculado com sucesso!", "success");
                setOpenLinkModal(false);
                loadProcessosTransitorios();
                loadProcessosOficiais();
            })
            .catch(err => {
                notify(err.message || "Erro ao vincular processo.", "error");
            })
            .finally(() => setLinking(false));
    };

    const formatCurrency = (val) => {
        if (val == null || val === "") return "-";
        return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(val);
    };

    const formatDate = (val) => {
        if (!val) return "-";
        try {
            const d = new Date(val);
            if (isNaN(d.getTime())) return "-";
            return d.toLocaleDateString("pt-BR", { timeZone: "UTC" });
        } catch (e) {
            return "-";
        }
    };

    const formatDateTime = (val) => {
        if (!val) return "-";
        try {
            const d = new Date(val);
            if (isNaN(d.getTime())) return "-";
            return d.toLocaleString("pt-BR");
        } catch (e) {
            return String(val);
        }
    };

    return (
        <MDBox p={1}>
            {/* SEÇÃO 1: PROCESSOS DA FICHA / TRANSITÓRIOS */}
            <MDBox mb={4}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <MDBox display="flex" alignItems="center" gap={1}>
                        <Icon sx={{ color: darkMode ? "#90caf9" : "#1976d2", fontSize: "24px !important" }}>description</Icon>
                        <MDBox>
                            <MDTypography variant="h6" fontWeight="medium" color={darkMode ? "white" : "dark"}>
                                Processos Ajuizados
                            </MDTypography>
                            <MDTypography variant="caption" color="text">
                                Registros migrados da BVJuridico ou apontados diretamente para a carteira deste devedor.
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                    <MDButton variant="gradient" color="info" size="small" startIcon={<Icon>add</Icon>} onClick={handleOpenNovo}>
                        Novo Processo
                    </MDButton>
                </MDBox>

                <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: "none", border: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e0e0e0", borderRadius: "8px", overflow: "hidden", background: darkMode ? "#4B4F5C" : "#ffffff" }}>
                    <Table size="small">
                        <MDBox component="thead">
                            <TableRow>
                                <DataTableHeadCell width="20%" sorted={false}>Número do Processo</DataTableHeadCell>
                                <DataTableHeadCell width="18%" sorted={false}>Tipo de Ação</DataTableHeadCell>
                                <DataTableHeadCell width="12%" sorted={false}>Contrato</DataTableHeadCell>
                                <DataTableHeadCell width="12%" sorted={false}>Data Distribuição</DataTableHeadCell>
                                <DataTableHeadCell width="14%" sorted={false}>Valor da Causa</DataTableHeadCell>
                                <DataTableHeadCell width="12%" sorted={false}>Carteira / Ficha</DataTableHeadCell>
                                <DataTableHeadCell width="12%" align="center" sorted={false}>Ações</DataTableHeadCell>
                            </TableRow>
                        </MDBox>
                        <TableBody>
                            {processosTransitorios.length === 0 && !loadingTransitorios && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 3, borderBottom: "none" }}>
                                        <MDTypography variant="caption" color="text">Nenhum processo transitório/ajuizado registrado para esta ficha.</MDTypography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {processosTransitorios.map((proc, index) => (
                                <TableRow key={proc.id || index} hover>
                                    <DataTableBodyCell isProcess={true}>
                                        <MDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "dark"}>
                                            {proc.numeroProcesso || "S/N"}
                                        </MDTypography>
                                    </DataTableBodyCell>
                                    <DataTableBodyCell isProcess={true}>
                                        <Chip
                                            label={proc.tipoAcao || "Ação de Cobrança"}
                                            size="small"
                                            sx={{
                                                fontSize: "0.68rem",
                                                height: 20,
                                                backgroundColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "#f0f2f5",
                                                color: darkMode ? "#ffffff" : "#333333",
                                                border: darkMode ? "1px solid rgba(255, 255, 255, 0.2)" : "1px solid #d0d0d0",
                                            }}
                                        />
                                    </DataTableBodyCell>
                                    <DataTableBodyCell isProcess={true}>
                                        <MDTypography variant="caption" color="text">
                                            {proc.contrato || "-"}
                                        </MDTypography>
                                    </DataTableBodyCell>
                                    <DataTableBodyCell isProcess={true}>
                                        <MDTypography variant="caption" color="text">
                                            {formatDate(proc.dataDistribuicao)}
                                        </MDTypography>
                                    </DataTableBodyCell>
                                    <DataTableBodyCell isProcess={true}>
                                        <MDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "dark"}>
                                            {formatCurrency(proc.valorCausa)}
                                        </MDTypography>
                                    </DataTableBodyCell>
                                    <DataTableBodyCell isProcess={true}>
                                        <MDTypography variant="caption" color={proc.isHonorario ? "warning" : "text"} fontWeight={proc.isHonorario ? "bold" : "regular"}>
                                            {proc.isHonorario ? "Honorários" : (proc.nomeCarteira || "Cobrança")}
                                        </MDTypography>
                                    </DataTableBodyCell>
                                    <DataTableBodyCell align="center" isProcess={true}>
                                        <Tooltip title="Histórico de Auditoria">
                                            <IconButton size="small" onClick={() => handleOpenHistorico(proc)} sx={{ p: 0.5, color: darkMode ? "#81c784" : "#2e7d32" }}>
                                                <Icon fontSize="small">history</Icon>
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Editar Processo">
                                            <IconButton size="small" onClick={() => handleOpenEditar(proc)} sx={{ p: 0.5, color: darkMode ? "#90caf9" : "#1976d2" }}>
                                                <Icon fontSize="small">edit</Icon>
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Vincular ao Módulo Jurídico Oficial (TbProcesso)">
                                            <IconButton size="small" onClick={() => handleOpenLink(proc)} sx={{ p: 0.5, color: darkMode ? "#ffb74d" : "#ef6c00" }}>
                                                <Icon fontSize="small">link</Icon>
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Excluir">
                                            <IconButton size="small" onClick={() => handleDelete(proc)} sx={{ p: 0.5, color: darkMode ? "#ef5350" : "#d32f2f" }}>
                                                <Icon fontSize="small">delete</Icon>
                                            </IconButton>
                                        </Tooltip>
                                    </DataTableBodyCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </MDBox>

            {/* SEÇÃO 2: PROCESSOS JUDICIAIS OFICIAIS (TBPROCESSO) */}
            <MDBox>
                <MDBox display="flex" alignItems="center" gap={1} mb={1.5}>
                    <Icon sx={{ color: darkMode ? "#ffb74d" : "#ef6c00", fontSize: "24px !important" }}>gavel</Icon>
                    <MDBox>
                        <MDTypography variant="h6" fontWeight="medium" color={darkMode ? "white" : "dark"}>
                            Processos no Jurídico
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                            Processos oficiais vinculados aos contratos através da estrutura corporativa do Jurídico.
                        </MDTypography>
                    </MDBox>
                </MDBox>

                <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: "none", border: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e0e0e0", borderRadius: "8px", overflow: "hidden", background: darkMode ? "#4B4F5C" : "#ffffff" }}>
                    <Table size="small">
                        <MDBox component="thead">
                            <TableRow>
                                <DataTableHeadCell width="25%" sorted={false}>Número do Processo</DataTableHeadCell>
                                <DataTableHeadCell width="15%" sorted={false}>Data Distribuição</DataTableHeadCell>
                                <DataTableHeadCell width="15%" sorted={false}>Valor da Causa</DataTableHeadCell>
                                <DataTableHeadCell width="15%" sorted={false}>Equipe Responsável</DataTableHeadCell>
                                <DataTableHeadCell width="15%" sorted={false}>Advogado Responsável</DataTableHeadCell>
                                <DataTableHeadCell width="15%" sorted={false}>Contratos Vinculados</DataTableHeadCell>
                            </TableRow>
                        </MDBox>
                        <TableBody>
                            {processosOficiais.length === 0 && !loadingOficiais && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3, borderBottom: "none" }}>
                                        <MDTypography variant="caption" color="text">Nenhum processo oficial do sistema vinculado a este devedor.</MDTypography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {processosOficiais.map((proc, index) => (
                                <TableRow key={index} hover>
                                    <DataTableBodyCell isProcess={true}>
                                        <MDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "dark"}>{proc.numeroProcesso}</MDTypography>
                                    </DataTableBodyCell>
                                    <DataTableBodyCell isProcess={true}>
                                        <MDTypography variant="caption" color="text">{formatDate(proc.dataDistribuicao)}</MDTypography>
                                    </DataTableBodyCell>
                                    <DataTableBodyCell isProcess={true}>
                                        <MDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "dark"}>{formatCurrency(proc.valorCausa)}</MDTypography>
                                    </DataTableBodyCell>
                                    <DataTableBodyCell isProcess={true}>
                                        <MDTypography variant="caption" color="text">{proc.equipeResponsavel || "-"}</MDTypography>
                                    </DataTableBodyCell>
                                    <DataTableBodyCell isProcess={true}>
                                        <MDTypography variant="caption" color="text">{proc.advogadoResponsavel || "-"}</MDTypography>
                                    </DataTableBodyCell>
                                    <DataTableBodyCell isProcess={true}>
                                        <MDTypography variant="caption" color="text">{proc.contratosLinked}</MDTypography>
                                    </DataTableBodyCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </MDBox>

            {/* DIALOG DE CADASTRO/EDIÇÃO MANUAL */}
            <Dialog 
                open={openModal} 
                onClose={() => !saving && setOpenModal(false)} 
                maxWidth="sm" 
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#4B4F5C" : "#ffffff",
                        color: darkMode ? "#ffffff" : "#000000",
                        borderRadius: "10px",
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1, color: darkMode ? "#ffffff" : "#000000" }}>
                    <MDTypography variant="h6" fontWeight="bold">
                        {formData.id ? "Editar Processo da Ficha" : "Cadastrar Processo na Ficha"}
                    </MDTypography>
                </DialogTitle>
                <DialogContent dividers sx={{ borderColor: darkMode ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)" }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={7}>
                            <MDInput
                                label="Número do Processo (CNJ)"
                                fullWidth
                                value={formData.numeroProcesso}
                                onChange={(e) => setFormData({ ...formData, numeroProcesso: e.target.value })}
                                placeholder="0000000-00.0000.0.00.0000"
                            />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <Autocomplete
                                options={acoesDisponiveis}
                                getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.nome || "")}
                                isOptionEqualToValue={(option, val) => option.id === val.id || option.nome === (val.nome || val)}
                                value={
                                    acoesDisponiveis.find(a => a.id === formData.codAcao) ||
                                    (formData.tipoAcao ? { id: formData.codAcao, nome: formData.tipoAcao } : null)
                                }
                                onChange={(_, val) => {
                                    if (typeof val === "string") {
                                        setFormData({ ...formData, tipoAcao: val, codAcao: null });
                                    } else if (val && val.id) {
                                        setFormData({ ...formData, tipoAcao: val.nome, codAcao: val.id });
                                    } else {
                                        setFormData({ ...formData, tipoAcao: "", codAcao: null });
                                    }
                                }}
                                freeSolo
                                renderInput={(params) => (
                                    <MDInput {...params} label="Tipo de Ação" fullWidth placeholder="Selecione ou digite..." />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <MDInput
                                label="Nº do Contrato"
                                fullWidth
                                value={formData.contrato}
                                onChange={(e) => setFormData({ ...formData, contrato: e.target.value })}
                                placeholder="Contrato vinculado"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <MDInput
                                type="date"
                                label="Data de Distribuição"
                                fullWidth
                                value={formData.dataDistribuicao}
                                onChange={(e) => setFormData({ ...formData, dataDistribuicao: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                sx={darkMode ? { "& input": { colorScheme: "dark" } } : {}}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <MDInput
                                type="number"
                                label="Valor da Causa (R$)"
                                fullWidth
                                value={formData.valorCausa}
                                onChange={(e) => setFormData({ ...formData, valorCausa: e.target.value })}
                                placeholder="0.00"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <MDInput
                                multiline
                                rows={2}
                                label="Observações"
                                fullWidth
                                value={formData.observacao}
                                onChange={(e) => setFormData({ ...formData, observacao: e.target.value })}
                                placeholder="Anotações sobre o ajuizamento..."
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #f0f2f5" }}>
                    <MDButton 
                        color="secondary" 
                        variant="outlined" 
                        disabled={saving} 
                        onClick={() => setOpenModal(false)}
                        sx={{
                            color: darkMode ? "#ffffff !important" : undefined,
                            borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined,
                            "&:hover": {
                                borderColor: darkMode ? "#ffffff !important" : undefined,
                                backgroundColor: darkMode ? "rgba(255, 255, 255, 0.08) !important" : undefined,
                            }
                        }}
                    >
                        Cancelar
                    </MDButton>
                    <MDButton color="info" variant="gradient" disabled={saving} onClick={handleSave}>
                        {saving ? "Salvando..." : "Salvar Processo"}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* DIALOG DE HISTÓRICO / AUDITORIA */}
            <Dialog
                open={openAuditModal}
                onClose={() => setOpenAuditModal(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#4B4F5C" : "#ffffff",
                        color: darkMode ? "#ffffff" : "#000000",
                        borderRadius: "10px",
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1, color: darkMode ? "#ffffff" : "#000000", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <MDBox display="flex" alignItems="center" gap={1}>
                        <Icon color="success">history</Icon>
                        <MDTypography variant="h6" fontWeight="bold">
                            Auditoria de Alterações do Processo
                        </MDTypography>
                    </MDBox>
                    <IconButton size="small" onClick={() => setOpenAuditModal(false)}>
                        <Icon>close</Icon>
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ borderColor: darkMode ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)" }}>
                    <MDBox mb={2}>
                        <MDTypography variant="subtitle2" color="text">
                            Processo: <strong>{selectedProcessoAudit?.numeroProcesso || "S/N"}</strong>
                            {selectedProcessoAudit?.contrato ? ` (Contrato: ${selectedProcessoAudit.contrato})` : ""}
                        </MDTypography>
                    </MDBox>

                    {loadingAudit ? (
                        <MDBox display="flex" justifyContent="center" alignItems="center" p={4}>
                            <CircularProgress size={30} />
                        </MDBox>
                    ) : auditLogs.length === 0 ? (
                        <MDBox textAlign="center" py={4}>
                            <MDTypography variant="body2" color="text">
                                Nenhuma alteração registrada no histórico deste processo.
                            </MDTypography>
                        </MDBox>
                    ) : (
                        auditLogs.map((log, idx) => (
                            <Paper
                                key={log.id || idx}
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "#fafafa",
                                    borderColor: darkMode ? "rgba(255, 255, 255, 0.12)" : "#e0e0e0"
                                }}
                            >
                                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <MDBox display="flex" alignItems="center" gap={1}>
                                        <Icon fontSize="small" sx={{ color: darkMode ? "#90caf9" : "#1976d2" }}>person</Icon>
                                        <MDTypography variant="button" fontWeight="medium" color={darkMode ? "white" : "dark"}>
                                            {log.usuario || "Sistema"}
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox display="flex" alignItems="center" gap={0.5}>
                                        <Icon fontSize="small" color="text">access_time</Icon>
                                        <MDTypography variant="caption" color="text">
                                            {formatDateTime(log.data)}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>

                                {Array.isArray(log.alteracoes) ? (
                                    <TableContainer>
                                        <Table size="small">
                                            <MDBox component="thead">
                                                <TableRow>
                                                    <DataTableHeadCell width="30%" sorted={false}>Campo Alterado</DataTableHeadCell>
                                                    <DataTableHeadCell width="35%" sorted={false}>Valor Anterior</DataTableHeadCell>
                                                    <DataTableHeadCell width="35%" sorted={false}>Novo Valor</DataTableHeadCell>
                                                </TableRow>
                                            </MDBox>
                                            <TableBody>
                                                {log.alteracoes.map((alt, aIdx) => (
                                                    <TableRow key={aIdx}>
                                                        <DataTableBodyCell isProcess={true}>
                                                            <MDTypography variant="caption" fontWeight="medium" color={darkMode ? "white" : "dark"}>
                                                                {alt.campo || "-"}
                                                            </MDTypography>
                                                        </DataTableBodyCell>
                                                        <DataTableBodyCell isProcess={true}>
                                                            <MDTypography variant="caption" color="text" sx={{ textDecoration: "line-through" }}>
                                                                {String(alt.de != null ? alt.de : "-")}
                                                            </MDTypography>
                                                        </DataTableBodyCell>
                                                        <DataTableBodyCell isProcess={true}>
                                                            <MDTypography variant="caption" color="success" fontWeight="medium">
                                                                {String(alt.para != null ? alt.para : "-")}
                                                            </MDTypography>
                                                        </DataTableBodyCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <MDTypography variant="caption" color="text">
                                        {String(log.alteracoes || "Alteração registrada")}
                                    </MDTypography>
                                )}
                            </Paper>
                        ))
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #f0f2f5" }}>
                    <MDButton 
                        color="secondary" 
                        variant="outlined" 
                        onClick={() => setOpenAuditModal(false)}
                        sx={{
                            color: darkMode ? "#ffffff !important" : undefined,
                            borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined,
                            "&:hover": {
                                borderColor: darkMode ? "#ffffff !important" : undefined,
                                backgroundColor: darkMode ? "rgba(255, 255, 255, 0.08) !important" : undefined,
                            }
                        }}
                    >
                        Fechar
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* DIALOG DE VINCULAÇÃO AO PROCESSO REAL */}
            <Dialog 
                open={openLinkModal} 
                onClose={() => !linking && setOpenLinkModal(false)} 
                maxWidth="xs" 
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#4B4F5C" : "#ffffff",
                        color: darkMode ? "#ffffff" : "#000000",
                        borderRadius: "10px",
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1, color: darkMode ? "#ffffff" : "#000000" }}>
                    <MDTypography variant="h6" fontWeight="bold">
                        Vincular ao Módulo Jurídico Oficial
                    </MDTypography>
                </DialogTitle>
                <DialogContent dividers sx={{ borderColor: darkMode ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)" }}>
                    <MDBox mb={2}>
                        <MDTypography variant="caption" color="text">
                            Processo Selecionado: <strong>{selectedTransitorio?.numeroProcesso || selectedTransitorio?.contrato}</strong>
                        </MDTypography>
                    </MDBox>
                    <MDInput
                        label="ID do Processo Real (TbProcesso)"
                        fullWidth
                        value={idProcessoRealInput}
                        onChange={(e) => setIdProcessoRealInput(e.target.value)}
                        placeholder="Informe o ID do Processo Oficial..."
                    />
                    <MDBox mt={1.5}>
                        <MDTypography variant="caption" color="text" sx={{ fontSize: "0.7rem" }}>
                            * Ao confirmar a vinculação, os contratos da ficha serão ligados ao processo judicial oficial e este registro provisório será finalizado.
                        </MDTypography>
                    </MDBox>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #f0f2f5" }}>
                    <MDButton 
                        color="secondary" 
                        variant="outlined" 
                        disabled={linking} 
                        onClick={() => setOpenLinkModal(false)}
                        sx={{
                            color: darkMode ? "#ffffff !important" : undefined,
                            borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined,
                            "&:hover": {
                                borderColor: darkMode ? "#ffffff !important" : undefined,
                                backgroundColor: darkMode ? "rgba(255, 255, 255, 0.08) !important" : undefined,
                            }
                        }}
                    >
                        Cancelar
                    </MDButton>
                    <MDButton color="warning" variant="gradient" disabled={linking} onClick={handleConfirmLink}>
                        {linking ? "Vinculando..." : "Confirmar Vínculo"}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* SNACKBAR DE AVISOS */}
            <MDSnackbar
                color={sbColor}
                icon="notifications"
                title="Processos"
                content={sbMessage}
                open={sbOpen}
                onClose={() => setSbOpen(false)}
                close={() => setSbOpen(false)}
                bgWhite={!darkMode}
            />
        </MDBox>
    );
}
